// Lógica pura de la fórmula de indexación (apartado 3.4 Anexo Orden FOM/1882/2012,
// modificado por RD-ley 9/2026). Sin dependencias del DOM ni de Angular para que
// `bun test` pueda importar este módulo directamente.

export const FECHA_TRAMO_3 = new Date('2026-04-15');
export const FIN_TRANSITORIO = new Date('2026-06-30');
export const UMBRAL_AUTOMATICO_PCT = 5;

// Para vehículos CON derecho a gasóleo profesional: PAI = precio − (IH 0,379 − dev. 0,049) = precio − 0,330
// Para vehículos SIN derecho: PAI = precio − IH completo (0,379)
export const IH_NETO_PROF = 0.330;
export const IH_COMPLETO = 0.379;

export type Categoria = 'mayor20' | 'entre' | 'obras' | 'ligero';

interface TramoC {
  hasta: number; // PAI < hasta → C
  C: number;
}

// Tablas oficiales del coeficiente C (RD-ley 9/2026, apartado 3.4).
export const TABLAS_C: Record<Categoria, TramoC[]> = {
  mayor20: [
    { hasta: 0.85, C: 0.3 },
    { hasta: 1.40, C: 0.4 },
    { hasta: Infinity, C: 0.5 },
  ],
  entre: [
    { hasta: 0.95, C: 0.2 },
    { hasta: 1.80, C: 0.3 },
    { hasta: Infinity, C: 0.4 },
  ],
  obras: [
    { hasta: 0.75, C: 0.2 },
    { hasta: 1.45, C: 0.3 },
    { hasta: Infinity, C: 0.4 },
  ],
  ligero: [
    { hasta: 0.70, C: 0.1 },
    { hasta: 1.95, C: 0.2 },
    { hasta: Infinity, C: 0.3 },
  ],
};

// Extrae el PAI a partir del precio MITMS (que viene como PAI+IH-GP).
// Hasta el 30/06/2026 (régimen transitorio del RD-ley 9/2026) siempre se resta 0,330,
// independientemente del derecho a gasóleo profesional.
export function calcularPAI(
  precio: number,
  fechaEjecucion: Date | null | undefined,
  gasoleoProf: boolean,
): number {
  if (!fechaEjecucion || fechaEjecucion <= FIN_TRANSITORIO) {
    return precio - IH_NETO_PROF;
  }
  return gasoleoProf ? precio - IH_NETO_PROF : precio - IH_COMPLETO;
}

export function obtenerCoeficienteC(categoria: Categoria, PAI: number): number {
  const tabla = TABLAS_C[categoria];
  for (const t of tabla) {
    if (PAI < t.hasta) return t.C;
  }
  return tabla[tabla.length - 1].C;
}

export interface IndexacionInput {
  precioFirma: number;
  precioEjecucion: number;
  fechaEjecucion: Date;
  categoria: Categoria;
  gasoleoProf: boolean;
}

export interface IndexacionBase {
  G_pct: number; // variación porcentual del gasóleo (firma → ejecución)
  PAI: number;   // €/l del día de ejecución
  C: number;     // coeficiente de la tabla
}

export function calcularBase({
  precioFirma,
  precioEjecucion,
  fechaEjecucion,
  categoria,
  gasoleoProf,
}: IndexacionInput): IndexacionBase {
  const G_pct = ((precioEjecucion / precioFirma) - 1) * 100;
  const PAI = calcularPAI(precioEjecucion, fechaEjecucion, gasoleoProf);
  const C = obtenerCoeficienteC(categoria, PAI);
  return { G_pct, PAI, C };
}

// ΔP = G × P × C / 100. Con G en porcentaje (no en tanto por uno).
export function calcularDeltaP(G_pct: number, importe: number, C: number): number {
  return (G_pct * importe * C) / 100;
}
